/**
 * Unpin confirmation.
 *
 * Shows the message being unpinned, because a pin can sit hundreds of messages back in the
 * thread and the "Unpin" action is a hover target on a dense list — "which one did I just
 * click?" is a real question. Unpin is otherwise recoverable (re-pin), so the wording is a
 * plain "remove", not the delete dialog's "cannot be undone".
 */

import {
  BlockScroll,
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { toast } from "@birdwatch/ui/toast";
import { LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button/button";
import { Markdown } from "@/components/ui/markdown";
import { Message } from "@/components/ui/message";
import type { ChannelMessage } from "../_api/channel-endpoints";
import { ChannelAuthorAvatar, ChannelAuthorName } from "./channel-author";

type ChannelMessageUnpinDialogProps = {
  message: ChannelMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isUnpinning: boolean;
  onConfirm: (messageId: string) => Promise<unknown>;
  unpinConfirm?: { title?: string; description?: string };
};

export function ChannelMessageUnpinDialog({
  message,
  open,
  onOpenChange,
  isUnpinning,
  onConfirm,
  unpinConfirm,
}: ChannelMessageUnpinDialogProps) {
  if (!message) return null;

  const title = unpinConfirm?.title ?? "Remove pinned message";
  const description =
    unpinConfirm?.description ??
    "Are you sure you want to remove this pinned message?";

  const handleConfirm = async () => {
    try {
      await onConfirm(message.id);
      onOpenChange(false);
      toast.success({ title: "Message unpinned!" });
    } catch (error) {
      toast.error({ title: "Can't unpin message!" });
      console.error(error);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        dialogProps={{ onOpenAutoFocus: (event) => event.preventDefault() }}
        className="sm:max-w-md flex flex-col gap-0 p-0"
      >
        <ModalHeader className="text-left">
          <ModalTitle className="p-0">{title}</ModalTitle>
          <ModalDescription className="sr-only">{title}</ModalDescription>
        </ModalHeader>
        <BlockScroll>
          <div className="p-4 pt-1 space-y-4">
            <p className="text-sm text-text-dark-high">{description}</p>
            <Message
              avatar={<ChannelAuthorAvatar userId={message.userCreatedId} />}
              userCreateName={
                <ChannelAuthorName userId={message.userCreatedId} />
              }
              viaClient={message.viaClient ?? ""}
              createdAt={message.createdAt}
              updatedAt={message.updatedAt}
              className="px-3 border border-border-dark-low rounded-2xl"
            >
              <Markdown
                content={message.body ?? ""}
                className="text-text-dark-max line-clamp-2"
              />
            </Message>
          </div>
        </BlockScroll>
        <ModalFooter>
          <ModalClose asChild>
            <Button type="button" kind="outline" variant="neutral">
              Cancel
            </Button>
          </ModalClose>
          <Button
            variant="negative"
            onClick={handleConfirm}
            disabled={isUnpinning}
          >
            {isUnpinning && <LoaderCircle className="animate-spin" />}
            Remove
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
